<template>
  <article class="plan-card">
    <div
      v-if="plan || subscription || isBacker"
      :class="[`plan-card card position-relative ${colorSet.cardColor}`, { 'with-top-tag': isCurrentPlan }]"
      style="--link-color: var(--fa-navy); --card-color: var(--fa-navy)"
    >
      <plan-info
        :plan="plan"
        :subscription="subscription"
        :determinations="determinations"
        :price="price"
        :price-with-discount="priceWithDiscount"
        :discount="discount"
        :is-current-plan="isCurrentPlan"
        :color-set="colorSet"
        :is-upgrading="isUpgrading"
        :seats="seats"
        :has-limited-offer-badge="hasLimitedOfferBadge"
        :plan-icon-style="planIconStyle"
        :account-view="accountView"
      />

      <current-plan-badge
        v-if="isCurrentPlan"
        :plan="plan"
        :subscription="subscription"
        :determinations="determinations"
        :color-set="colorSet"
      />

      <plan-action
        v-if="allowPlanActions"
        :determinations="determinations"
        @setup-auto-renew="$emit('setup-auto-renew')"
        @add-payment-method="$emit('add-payment-method')"
        @update-payment-method="$emit('update-payment-method')"
      />

      <features
        :plan="plan"
        :subscription="subscription"
        :determinations="determinations"
        :seats="seats"
        :color-set="colorSet"
      />

      <services
        :determinations="determinations"
        :color-set="colorSet"
        :plan="plan"
      />

      <subscription-details
        v-if="isShowingSubscriptionDetails && subscription"
        :subscription="subscription"
      />
    </div>

    <additional-warnings
      :subscription="subscription"
      :determinations="determinations"
      :color-set="colorSet"
    />

    <card-footer
      :plan="plan"
      :subscription="subscription"
      :discount="discount"
      :price-with-discount="priceWithDiscount"
      :pre-order-backer-dates="preOrderBackerDates"
      :determinations="determinations"
      :allow-plan-actions="allowPlanActions"
      :allow-plan-change="allowPlanChange"
      :color-set="colorSet"
      @setup-auto-renew="$emit('setup-auto-renew')"
      @cancel="$emit('cancel')"
    />
  </article>
</template>

<script>
  import { every, get } from 'lodash'
  import moment from 'moment'
  import PlanInfo from './sections/PlanInfo'
  import PlanAction from './sections/PlanAction'
  import Features from './sections/Features'
  import Services from './sections/Services'
  import CurrentPlanBadge from './badges/CurrentPlanBadge'
  import SubscriptionDetails from './sections/SubscriptionDetails'
  import AdditionalWarnings from './sections/AdditionalWarnings'
  import CardFooter from './sections/CardFooter'
  import { resolve, colors } from './utils/colors'
  import { determinations } from 'app/utils/subscription'
  import { PLAN_ICONSTYLE_BY_SKU } from 'app/views/plans/PlansData.js'

  export default {
    name: 'PlanCard',

    components: {
      PlanInfo,
      PlanAction,
      Features,
      Services,
      CurrentPlanBadge,
      SubscriptionDetails,
      AdditionalWarnings,
      CardFooter
    },

    props: {
      plan: {
        type: Object
      },

      subscription: {
        type: Object
      },

      hasLimitedOfferBadge: {
        type: Boolean,
        default: false
      },

      isBacker: {
        type: Boolean,
        default: false
      },

      date: {
        type: Object,
        default: function () {
          return moment.utc()
        },
        validator: function (val) {
          return moment.isMoment(val)
        }
      },

      preOrderBackerDates: {
        type: Object,
        default: function () {
          return {
            'released': moment.parseZone(FONTAWESOME.BACKER_SERVICES_EXPIRATION_WARNING_START_DATE),
            'alerting': moment.parseZone(FONTAWESOME.BACKER_SERVICES_EXPIRATION_WARNING_START_DATE),
            'expires': moment.parseZone(FONTAWESOME.BACKER_SERVICES_EXPIRATION_GRACE_PERIOD_START_DATE),
            'grace-period': moment.parseZone(FONTAWESOME.BACKER_SERVICES_EXPIRATION_GRACE_PERIOD_START_DATE),
            'shutoff': moment.parseZone(FONTAWESOME.BACKER_SERVICES_EXPIRATION_DATE)
          }
        },
        validator: function (val) {
          return every(Object.keys(val).map((stage) => moment.isMoment(val[stage])))
        }
      },

      price: {
        type: Number
      },

      priceWithDiscount: {
        type: Number
      },

      seats: {
        type: Number
      },

      discount: {
        type: Number
      },

      allowPlanActions: {
        type: Boolean,
        default: true
      },

      allowPlanChange: {
        type: Boolean,
        default: false
      },

      isShowingSubscriptionDetails: {
        type: Boolean,
        default: false
      },

      isUpgrading: {
        type: Boolean,
        default: false
      },

      accountView: {
        type: Boolean,
        default: false
      }
    },

    // Declared per CMP-15: an undeclared native-named emit runs the parent's handler twice.
    emits: ['add-payment-method', 'cancel', 'setup-auto-renew', 'update-payment-method'],

    computed: {
      colorSet() {
        return resolve(this.waColorFamily)
      },

      determinations() {
        let preOrderBackerStage

        const stages = ['alerting', 'grace-period', 'shutoff']

        stages.forEach((stage) => {
          const stageDate = moment(this.preOrderBackerDates[stage])

          if (stageDate.isBefore(this.date)) {
            preOrderBackerStage = stage
          }
        })

        return determinations({
          subscription: this.subscription,
          isBacker: this.isBacker,
          preOrderBackerStage
        })
      },

      isCurrentPlan() {
        return !!(
          (!this.plan && this.isBacker) ||
          (!this.plan && this.subscription) ||
          get(this, ['plan', 'sku']) === get(this, ['subscription', 'plan', 'sku'])
        )
      },

      waColorFamily() {
        return colors({
          plan: get(this, ['plan']),
          subscription: get(this, ['subscription']),
          determinations: this.determinations
        })
      },

      isFree() {
        return get(this, ['subscription', 'type']) === 'free'
      },

      planIconStyle() {
        if (!this.plan) {
          return 'solid'
        } else {
          return get(PLAN_ICONSTYLE_BY_SKU, [this.plan.sku], 'solid')
        }
      }
    }
  }
</script>
